import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ViewType = 'dashboard' | 'health' | 'chat' | 'protocols' | 'insights' | 'plan' | 'achievements' | 'profile';

interface UIState {
  activeView: ViewType;
  isMobile: boolean;
  isSidebarOpen: boolean;
  modals: {
    preferences: boolean;
    integrations: boolean;
    workspace: boolean;
    privacy: boolean;
    guides: boolean;
    safety: boolean;
    score: boolean;
    referral: boolean;
  };
  globalSearch: {
    isOpen: boolean;
    query: string;
  };
  notifications: {
    error: string | null;
    success: string | null;
    info: string | null;
  };
}

const initialState: UIState = {
  activeView: 'dashboard',
  isMobile: false,
  isSidebarOpen: true,
  modals: {
    preferences: false,
    integrations: false,
    workspace: false,
    privacy: false,
    guides: false,
    safety: false,
    score: false,
    referral: false,
  },
  globalSearch: {
    isOpen: false,
    query: '',
  },
  notifications: {
    error: null,
    success: null,
    info: null,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setActiveView: (state, action: PayloadAction<ViewType>) => {
      state.activeView = action.payload;
    },
    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },
    openModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = false;
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key as keyof UIState['modals']] = false;
      });
    },
    setGlobalSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.globalSearch.isOpen = action.payload;
    },
    setGlobalSearchQuery: (state, action: PayloadAction<string>) => {
      state.globalSearch.query = action.payload;
    },
    setErrorNotification: (state, action: PayloadAction<string | null>) => {
      state.notifications.error = action.payload;
    },
    setSuccessNotification: (state, action: PayloadAction<string | null>) => {
      state.notifications.success = action.payload;
    },
    setInfoNotification: (state, action: PayloadAction<string | null>) => {
      state.notifications.info = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications.error = null;
      state.notifications.success = null;
      state.notifications.info = null;
    },
  },
});

export const {
  setActiveView,
  setIsMobile,
  setSidebarOpen,
  openModal,
  closeModal,
  closeAllModals,
  setGlobalSearchOpen,
  setGlobalSearchQuery,
  setErrorNotification,
  setSuccessNotification,
  setInfoNotification,
  clearNotifications,
} = uiSlice.actions;

export default uiSlice.reducer;
